import React, { useEffect, useState } from 'react';

import { usePublicCompany } from 'client/hooks';
import Coupon from 'components/Coupon/Coupon';
import { currentLanguage } from 'helpers/utils';
import usePublicApi from 'public-api';
import clientRoutes from 'routes/codegen/clientRoutes';
import { useAsyncFunction } from 'stores/hooks';
import { Button, Card, Icon } from 'ui';

import CallRequestModal from './CallRequestModal';

type Props = {
  ticket: BackendPublic.TicketDetail;
};

function Remarketing({ ticket }: Props) {
  const publicApi = usePublicApi();
  const { company } = usePublicCompany();

  const [settings, setSettings] = useState<BackendPublic.RemarketingSettings>();
  const [callRequested, setCallRequested] = useState(ticket.call_requested);
  const [phoneNumber, setPhoneNumber] = useState<string | undefined>(ticket.customer_phone);
  const [callRequestModalOpen, setCallRequestModalOpen] = useState(false);

  const [loadRemarketing] = useAsyncFunction(async () => {
    setSettings(
      await publicApi.MarketplaceRemarketingSettingsApi.retrieve({
        ticket_id: ticket.id,
      }),
    );
  });

  const [requestCall, isLoadingRequestCall] = useAsyncFunction(async () => {
    if (ticket.customer_phone && settings) {
      await publicApi.MarketplaceRemarketingCallRequestApi.create({
        settings_id: settings.id,
        request: { ticket_id: ticket.id },
      });
      setCallRequested(true);
    } else {
      setCallRequestModalOpen(true);
    }
  });

  useEffect(() => {
    if (ticket) {
      loadRemarketing();
    }
  }, [ticket]);

  if (!settings) {
    return null;
  }

  const getTicketVoucherSettings = () => settings?.voucher_settings?.[ticket.currency];

  const getExpirationDate = () => {
    if (ticket.voucher) {
      return ticket.voucher.valid_to ? new Date(ticket.voucher.valid_to) : null;
    }

    const voucherSettings = getTicketVoucherSettings();
    if (!voucherSettings?.expiration_days) {
      return null;
    }

    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + voucherSettings.expiration_days);
    return expirationDate;
  };

  const displayCallRequest = !ticket.closed_at && settings.call_request_in_ticket_detail && settings.call_request_text;
  const displayBanner = settings.banner_in_ticket_detail && settings.banner_url && settings.banner_image;
  const displayVoucher = ticket.voucher || settings.can_create_voucher;

  return (
    <>
      {displayVoucher && (
        <div className="flex justify-center">
          <Coupon
            ticketId={ticket.id}
            remarketingSettingsId={settings.id}
            shopName={currentLanguage(company?.name)}
            color="#4F46E5"
            textColor="#FFFFFF"
            voucherCode={ticket.voucher?.code}
            value={ticket.voucher ? ticket.voucher.amount : parseFloat(getTicketVoucherSettings()?.value)}
            currency={ticket.voucher ? ticket.voucher.currency : ticket.currency}
            expirationDate={getExpirationDate()}
            min_price={
              ticket.voucher && 'min_price' in ticket.voucher
                ? ticket.voucher.min_price
                : getTicketVoucherSettings()?.min_order_value
            }
          />
        </div>
      )}
      {displayCallRequest && (
        <>
          <Card flat>
            <div className="p-6">
              <div className="text-base font-medium leading-7 text-gray-900">
                <span>{gettext('Should we call you?')}</span>
              </div>
              {!callRequested && (
                <>
                  <div className="mt-1 text-sm text-gray-500">{currentLanguage(settings.call_request_text)}</div>
                  <div className="mt-4">
                    <Button onClick={requestCall} icon="phone" iconSolid loading={isLoadingRequestCall}>
                      {gettext('Call me')}
                    </Button>
                  </div>
                </>
              )}
              {callRequested && (
                <div className="mt-2 flex items-center text-sm text-gray-500">
                  <div className="mr-2 flex h-4 w-4 items-center text-green-600">
                    <Icon name="check" size="tiny" fitted />
                  </div>
                  {gettext('We have received your request and our agents will contact you soon on this phone number')}:
                  <span className="ml-1 font-bold">{phoneNumber}</span>
                </div>
              )}
            </div>
          </Card>
          <CallRequestModal
            remarketingSettingsId={settings.id}
            ticket={ticket}
            open={callRequestModalOpen}
            onClose={() => setCallRequestModalOpen(false)}
            onSuccess={(phone) => {
              setPhoneNumber(phone);
              setCallRequested(true);
              setCallRequestModalOpen(false);
            }}
          />
        </>
      )}
      {displayBanner && (
        <div className="flex justify-center">
          <a
            href={clientRoutes.marketplaceBanner({ remarketingSettingsId: settings.id, source: 'ticket-detail' })}
            target="_blank"
            rel="noreferrer"
          >
            <img src={settings.banner_image_url} alt="banner" />
          </a>
        </div>
      )}
    </>
  );
}

export default Remarketing;
